<template>
  <div v-show="menuOpen" id="userMenu" class="dropdown-menu" role="menu" ref="rootEl">
    <!-- Close button for mobile -->
    <button
      class="mobile-close-btn"
      @click="emit('closeMenu')"
      :aria-label="t('HEADER.DROPDOWN_MENU.close')"
    >
      <i class="fa-solid fa-xmark"></i>
    </button>

    <!-- Header -->
    <div class="menuHeader">
      <div class="profile-info">
        <div class="avatar-area" :class="{ 'avatar-area--vip': profile?.isVip }">
          <img class="avatar" :src="profile?.avatar" :alt="t('HEADER.DROPDOWN_MENU.avatarAlt')" />
          <div class="level">{{ profile.currentLevel }}</div>
        </div>
        <div class="details">
          <div class="username">
            <a :href="profile.profileUrl" target="_blank" rel="noopener" class="username-link">
              <span class="username__text">{{ profile?.name }}</span>
              <span v-if="profile?.isVip" class="username__badge">
                <VipBadge />
              </span>
              <i class="fa-brands fa-steam-symbol"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Account  -->
    <div class="accountWrapper" role="group" :aria-label="t('HEADER.DROPDOWN_MENU.account.title')">
      <span class="section-title">{{ t('HEADER.DROPDOWN_MENU.account.title') }}</span>
      <div class="linkWrapper">
        <router-link
          :to="{ name: RouteName.PROFILE }"
          class="link"
          :aria-label="t('HEADER.DROPDOWN_MENU.account.myProfile')"
          @click="emit('closeMenu')"
        >
          <i class="fa-solid fa-user"></i>
          {{ t('HEADER.DROPDOWN_MENU.account.myProfile') }}
        </router-link>

        <router-link
          :to="{ name: RouteName.PROFILE, params: { tab: 'affiliate' } }"
          class="link"
          :aria-label="t('HEADER.DROPDOWN_MENU.account.affiliate')"
          @click="emit('closeMenu')"
        >
          <i class="fa-solid fa-users-line"></i>
          {{ t('HEADER.DROPDOWN_MENU.account.affiliate') }}
        </router-link>

        <div
          class="link"
          @click="openDepositModal"
          role="button"
          tabindex="0"
          :aria-label="t('HEADER.DROPDOWN_MENU.account.usePromo')"
        >
          <i class="fa-solid fa-ticket"></i>
          {{ t('HEADER.DROPDOWN_MENU.account.usePromo') }}
        </div>
        <!--
        <div class="link">
          <i class="fa-solid fa-shield-halved"></i>
          {{ t('HEADER.DROPDOWN_MENU.account.securitySettings') }}
        </div>
        -->
      </div>
    </div>

    <!-- Banner -->
    <div class="bannerWrapper">
      <img :src="banner" :alt="t('HEADER.DROPDOWN_MENU.bannerAlt')" />
    </div>

    <!-- Others -->
    <div class="accountWrapper" role="group" :aria-label="t('HEADER.DROPDOWN_MENU.other.title')">
      <span class="section-title">{{ t('HEADER.DROPDOWN_MENU.other.title') }}</span>
      <div class="linkWrapper">
        <router-link
          class="link"
          :to="{ name: RouteName.SUPPORT }"
          :aria-label="t('HEADER.DROPDOWN_MENU.other.support')"
          @click="emit('closeMenu')"
        >
          <i class="fa-solid fa-headset"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.support') }}
        </router-link>
        <router-link
          class="link"
          :to="{ name: RouteName.ALPHA_WHALES }"
          :aria-label="t('HEADER.DROPDOWN_MENU.other.alphaWhalesClub')"
          @click="emit('closeMenu')"
        >
          <i class="fa-solid fa-gem"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.alphaWhalesClub') }}
        </router-link>
        <router-link
          class="link"
          :to="{ name: RouteName.PROVABLY_FAIR }"
          :aria-label="t('HEADER.DROPDOWN_MENU.other.provablyFair')"
          @click="emit('closeMenu')"
        >
          <i class="fa-solid fa-certificate"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.provablyFair') }}
        </router-link>
      </div>
    </div>

    <!-- Logout -->
    <div class="logoutWrapper">
      <a
        class="link"
        @click="handleLogout"
        :href="logoutUrl"
        :aria-label="t('HEADER.DROPDOWN_MENU.logout')"
      >
        <i class="fa-solid fa-right-from-bracket"></i>
        {{ t('HEADER.DROPDOWN_MENU.logout') }}
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from '@/store/app'
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useProfileStore } from '@/store/profile'
import { useDepositStore } from '@/store/deposit'
import { RouteName } from '@/router/types'
import { trackDepositInitiate } from '@/utils/gtagEvent'
import VipBadge from '@/components/VipBadge/VipBadge.vue'

import banner from '@/assets/banners/12.webp'
import { useI18n } from 'vue-i18n'

const { t, n } = useI18n()

const appStore = useAppStore()
const depositStore = useDepositStore()
const profileStore = useProfileStore()
const { profile } = storeToRefs(profileStore)

// Define emits
const emit = defineEmits(['closeMenu'])

const logoutUrl = ref(import.meta.env.VITE_API_URL + '/v1/auth/logout')

const openDepositModal = () => {
  trackDepositInitiate({ method: 'crypto', amount: 0, bonus_code_used: false })
  depositStore.setDepositMethod('promo-code')
  appStore.setShowDepositModal(true)
  emit('closeMenu')
}

const rootEl = ref<HTMLDivElement | null>(null)

defineProps<{
  menuOpen: boolean
  handleLogout: () => void
  closeMenu: () => void
}>()
defineExpose({ rootEl })
</script>

<style lang="scss" scoped>
@use 'sass:color';
.dropdown-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  width: 700px;
  max-width: 90vw;
  background: $surface;
  border: 1px solid color.scale($elevation1, $lightness: 12%);
  box-shadow:
    0 12px 24px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  z-index: 9999;
  display: flex;
  flex-direction: column;

  @include mobile {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 100vh;
    border: none;
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .mobile-close-btn {
    display: none;

    @include mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 16px;
      right: 16px;
      width: 40px;
      height: 40px;
      background: color.scale($surface, $lightness: 10%);
      border: 1px solid color.scale($elevation1, $lightness: 12%);
      border-radius: 50%;
      color: $on-surface;
      font-size: 20px;
      cursor: pointer;
      z-index: 10;
      transition: background-color 0.2s ease;

      &:hover {
        background: color.scale($surface, $lightness: 15%);
      }

      &:active {
        background: color.scale($surface, $lightness: 5%);
      }
    }
  }

  .menuHeader {
    display: flex;
    flex-direction: column;
    width: 100%;
    background: $surface;
    gap: 12px;

    // Profile Info
    .profile-info {
      display: flex;
      align-items: center;

      gap: 12px;
      padding: 16px;
      width: 100%;

      .avatar-area {
        position: relative;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        @include mobile {
          width: 64px;
          height: 64px;
        }

        .level {
          position: absolute;
          bottom: -12px;
          right: 50%;
          transform: translateX(50%);
          background: $accent-lime-2;
          color: $surface;
          font-weight: 700;
          font-size: 12px;
          padding: 2px 6px;
          border-radius: 12px;
          border: 2px solid $surface;
        }

        .avatar {
          border-radius: 50%;
          object-fit: cover;
          width: 100%;
          border: 2px solid $accent-lime-2;
          display: block;
          transition:
            border-color 0.3s ease,
            box-shadow 0.3s ease;
        }

        &.avatar-area--vip {
          .avatar {
            border-color: #ffd580;
            box-shadow:
              0 0 0 1px rgba(255, 222, 173, 0.2),
              0 0 8px rgba(255, 214, 128, 0.65),
              0 0 18px rgba(255, 190, 85, 0.45);
            animation: vipPulse 2s ease-in-out infinite;
          }

          .level {
            background: #ffd580;
            color: #1f1f1f;
          }
        }
      }

      .details {
        display: flex;
        justify-content: space-between;
        gap: 4px;

        a {
          text-decoration: none;
        }

        .username {
          display: flex;
          align-items: center;
          font-weight: 700;
          color: $on-surface;

          .username-link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 16px;
            color: inherit;
            text-decoration: none;

            i {
              background-color: #fff;
              border-radius: 50%;
              padding: 2px;
              font-size: 14px;
              color: $surface;
            }
          }

          .username__badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding-left: 2px;

            :deep(.vip-badge i) {
              font-size: 1rem;
              color: #ffd580;
            }
          }
        }

        .balance {
          display: flex;
          align-items: center;
          gap: 8px;
        }
      }
    }
  }
  .accountWrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    align-items: center;
    padding: 16px;
    background: color.scale($surface, $lightness: 4%);

    .section-title {
      font-size: 12px;
      font-weight: 700;
      color: color.scale($on-surface, $lightness: -20%);
      text-transform: uppercase;
      margin-bottom: 8px;
    }
    .linkWrapper {
      display: flex;
      width: 100%;
      gap: 12px;

      @media (max-width: 600px) {
        flex-direction: column;
      }

      .link {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 1rem;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        color: $on-surface;
        background: color.scale($surface, $lightness: 10%);
        width: 100%;
        text-decoration: none;

        i {
          font-size: 16px;
          width: 20px;
          text-align: center;
          color: color.scale($on-surface, $lightness: -10%);
        }

        &:hover {
          background-color: color.scale($elevation1, $lightness: 6%);
        }
      }
    }
  }

  .bannerWrapper {
    width: 100%;
    padding: 16px;
    background: color.scale($surface, $lightness: 4%);

    img {
      width: 100%;
      height: auto;
      display: block;
      border-radius: 8px;
    }
  }

  .logoutWrapper {
    display: flex;
    justify-content: center;
    width: 100%;
    border-top: 1px solid color.scale($elevation1, $lightness: 12%);
    padding: 16px;
    background: color.scale($surface, $lightness: 4%);

    .link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 8px 12px;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 500;
      color: $on-surface;
      width: 100%;
      text-decoration: none;

      i {
        font-size: 16px;
        width: 20px;
        text-align: center;
        color: color.scale($on-surface, $lightness: -10%);
      }
    }
  }
}

@keyframes vipPulse {
  0% {
    box-shadow:
      0 0 0 1px rgba(255, 222, 173, 0.2),
      0 0 8px rgba(255, 214, 128, 0.65),
      0 0 18px rgba(255, 190, 85, 0.45);
  }

  50% {
    box-shadow:
      0 0 0 2px rgba(255, 222, 173, 0.35),
      0 0 12px rgba(255, 214, 128, 0.85),
      0 0 24px rgba(255, 190, 85, 0.65);
  }

  100% {
    box-shadow:
      0 0 0 1px rgba(255, 222, 173, 0.2),
      0 0 8px rgba(255, 214, 128, 0.65),
      0 0 18px rgba(255, 190, 85, 0.45);
  }
}
</style>
